import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Dropdown } from "react-bootstrap";
import "./CreateProjectForm.css";
import { Select } from "antd";
import StbButton from "../../atoms/button/StbButton";
import { useFormik } from "formik";
import { ICreateProjectFormProps } from "./CreateProjectForm.types";
import {
  getGetApiProjectsQueryKey,
  usePostApiProjects,
} from "../../../api/generated/endpoints/project/project";
import {
  getGetApiErpImportGetAllProjectsByDepartmentQueryKey,
  getGetApiErpImportGetAllProjectsQueryKey,
} from "../../../api/generated/endpoints/erp-import/erp-import";
import { useGetApiStbusersGetTeamleaders } from "../../../api/generated/endpoints/stbuser/stbuser";
import type { ProjectCreateDto, STBUser } from "../../../api/generated/model";
import DateBox from "../../molecules/labelandtextbox/DateBox";
import { DEPARTMENT_OPTIONS } from "./departmentOptions";
import {
  emptyScaffoldingStepErrors,
  scaffoldingStepHasErrors,
  validateScaffoldingStep,
} from "../../../validations/scaffoldingStepValidation";

const CreateProjectForm: React.FC<ICreateProjectFormProps> = (props) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedEndDate, setSelectedEndDate] = useState<Date>();
  const [squareMeters, setSquareMeters] = useState<number>(0);
  const [accessRoutes, setAccessRoutes] = useState<number>(0);
  const [scaffoldingType, setScaffoldingType] = useState<string>("");
  const [scaffoldingIntendedFor, setScaffoldingIntendedFor] = useState<string>("");
  const [scaffoldingWidth, setScaffoldingWidth] = useState<string>("");
  const [scaffoldingHeight, setScaffoldingHeight] = useState<string>("");
  const selectedContactId = props.data.selectedContactId;
  const setSelectedContactsIds = props.setSelectedContactsIds;
  const customerContactList = props.customerContactList;
  const [includeMonteringsanalys, setIncludeMonteringsanalys] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [stepThreeErrors, setStepThreeErrors] = useState(emptyScaffoldingStepErrors());

  const createProjectMutation = usePostApiProjects({
    mutation: {
      onSuccess: async () => {
        if (props.queryClient && props.navigate) {
          await props.queryClient.invalidateQueries({
            queryKey: getGetApiProjectsQueryKey(),
          });
          await props.queryClient.invalidateQueries({
            queryKey: getGetApiErpImportGetAllProjectsQueryKey(),
          });
          await props.queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] ===
              getGetApiErpImportGetAllProjectsByDepartmentQueryKey()[0],
          });
          props.navigate("/projects");
        }
      },
    },
  });

  const { data: teamLeaders, isLoading: isLoadingTeamLeaders } = useGetApiStbusersGetTeamleaders({
    query: {
      staleTime: Infinity,
    },
  });

  // If the backend/ERP provides a preferred contact (`selectedContactId`),
  // preselect it only when nothing is selected yet.
  //
  // Important: don't overwrite the user's selection when the contact list refreshes
  // (e.g. after adding a new contact in the modal).
  useEffect(() => {
    if (!selectedContactId || !setSelectedContactsIds) return;
    if (props.selectedContactsIds.length > 0) return;

    const contactExists = customerContactList.some(
      (contact) => contact.id === selectedContactId
    );
    if (contactExists) {
      setSelectedContactsIds([selectedContactId]);
    }
  }, [
    selectedContactId,
    setSelectedContactsIds,
    customerContactList,
    props.selectedContactsIds,
  ]);

  // Add useEffect to handle mobile dropdown scrolling
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Check if touch started on any select component or its dropdown
      const isSelectElement = target.closest('.ant-select, .ant-select-dropdown, .dropdown-menu');
      if (isSelectElement) {
        (e as any).isDropdownTouch = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // If touch started on dropdown-related element, prevent page scroll
      if ((e as any).isDropdownTouch) {
        e.stopPropagation();
      }
    };

    // Use passive listeners to avoid cancelable warnings
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Simplified getPopupContainer function
  const getPopupContainer = (trigger: HTMLElement) => {
    return trigger.parentElement as HTMLElement;
  };

  const toDateKey = (date: Date) => date.toISOString().split("T")[0];

  const isEndDateAfterStartDate = (startDate: Date, endDate: Date) => {
    const startDateKey = toDateKey(startDate);
    const endDateKey = toDateKey(endDate);
    return endDateKey > startDateKey;
  };

  const formik = useFormik<ProjectCreateDto>({
    initialValues: {
      projectNumber: props.data.projectNumber ?? "",
      department: props.data.department,
      projectName: props.data.projectName ?? "",
      projectAddress: props.data.projectAddress ?? "",
      customerId: props.data.customerId,
      teamLeaderId: props.data.teamLeaderId ?? undefined,
      contacts: props.customerContactList,
      dateOfConstructionStart: undefined,
      dateOfConstructionEnd: undefined,
      squareMeters: squareMeters,
      accessRoutes: accessRoutes,
    },

    onSubmit: async (values) => {
      if (!selectedDate) {
        formik.setFieldTouched('dateOfConstructionStart', true, false);
        formik.setFieldError('dateOfConstructionStart', 'Välj ett datum för uppförande');
        return;
      }

      if (!selectedEndDate) {
        formik.setFieldTouched('dateOfConstructionEnd', true, false);
        formik.setFieldError('dateOfConstructionEnd', 'Välj slut datum');
        return;
      }

      if (!isEndDateAfterStartDate(selectedDate, selectedEndDate)) {
        formik.setFieldTouched('dateOfConstructionEnd', true, false);
        formik.setFieldError('dateOfConstructionEnd', 'Slut datum måste vara efter startdatum');
        return;
      }

      if (!values.department) {
        formik.setFieldError('department', 'Välj en avdelning');
        return;
      }

      if (props.selectedContactsIds.length === 0) {
        formik.setFieldError('contacts', 'Välj minst en kontaktperson');
        return;
      }

      if (!values.projectAddress || values.projectAddress.trim().length === 0) {
        formik.setFieldError('projectAddress', 'Skriv in en Projektadress');
        return;
      }

      const nextStepThreeErrors = validateScaffoldingStep({
        squareMeters,
        accessRoutes,
        scaffoldingType,
        scaffoldingIntendedFor,
        scaffoldingWidth,
        scaffoldingHeight,
      });

      if (scaffoldingStepHasErrors(nextStepThreeErrors)) {
        setStepThreeErrors(nextStepThreeErrors);
        setCurrentStep(3);
        return;
      }

      setStepThreeErrors(emptyScaffoldingStepErrors());

      const contactList = props.customerContactList.filter((contact) =>
        contact.id !== undefined && props.selectedContactsIds.includes(contact.id)
      );

      const dto: ProjectCreateDto = {
        projectNumber: props.data.projectNumber ?? "",
        department: values.department,
        projectName: props.data.projectName ?? "",
        projectAddress: values.projectAddress,
        customerId: props.data.customerId,
        teamLeaderId: values.teamLeaderId,
        contacts: contactList,
        dateOfConstructionStart: selectedDate.toISOString(),
        dateOfConstructionEnd: selectedEndDate.toISOString(),
        squareMeters: squareMeters,
        accessRoutes: accessRoutes,
        scaffoldingType: scaffoldingType,
        scaffoldingIntendedFor: scaffoldingIntendedFor,
        scaffoldingWidth: scaffoldingWidth,
        scaffoldingHeight: scaffoldingHeight,
        erpProjectRef: props.data.erpProjectRef,
        hasMonteringsanalys: includeMonteringsanalys,
      };

      try {
        createProjectMutation.mutate({ data: dto });
      } catch (error) {
        console.log(error);
      }
    },
  });

  const goBack = () => {
    props.setMaunallyCreateProject(false);
    if (props.navigate) {
      props.navigate("/createproject");
    }
  };

  if (props.error && !props.data.projectName) {
    return (
      <Container fluid className="mt-3 d-flex justify-content-center">
        <span>
          <h2>Error: {props.error.message}</h2>
        </span>
      </Container>
    );
  }

  const addContact = () => {
    props.setAddContactErrorMessage("");
    props.setIsModalAddContactVisible(true);
  };

  const editContact = () => {
    props.setAddContactErrorMessage("");
    props.setIsModalEditContactVisible(true);
  };

  const handleChangeContact = (values: number[]) => {
    props.setSelectedContactsIds(values);
  };

  const handleDateChange = (date: Date) => {
    if (date) {
      setSelectedDate(date);
      formik.setFieldTouched('dateOfConstructionStart', true, false);
      formik.setFieldError('dateOfConstructionStart', undefined);

      if (selectedEndDate && !isEndDateAfterStartDate(date, selectedEndDate)) {
        formik.setFieldTouched('dateOfConstructionEnd', true, false);
        formik.setFieldError('dateOfConstructionEnd', 'Slut datum måste vara efter startdatum');
      } else {
        formik.setFieldError('dateOfConstructionEnd', undefined);
      }
    }
  };

  const handleEndDateChange = (date: Date) => {
    if (date) {
      setSelectedEndDate(date);
      formik.setFieldTouched('dateOfConstructionEnd', true, false);

      if (selectedDate && !isEndDateAfterStartDate(selectedDate, date)) {
        formik.setFieldError('dateOfConstructionEnd', 'Slut datum måste vara efter startdatum');
      } else {
        formik.setFieldError('dateOfConstructionEnd', undefined);
      }
    }
  };

  const selectedDepartmentName = DEPARTMENT_OPTIONS.find(
    (d) => d.value === formik.values.department
  )?.label;

  const selectableDepartments = DEPARTMENT_OPTIONS;

  const nextStep = () => {
    if (currentStep === 2) {
      let hasErrors = false;

      if (!formik.values.department) {
        formik.setFieldTouched("department", true, false);
        formik.setFieldError("department", "Välj en avdelning");
        hasErrors = true;
      }

      if (!formik.values.teamLeaderId) {
        formik.setFieldTouched("teamLeaderId", true, false);
        formik.setFieldError("teamLeaderId", "Välj en arbetsledare");
        hasErrors = true;
      }

      if (!selectedDate) {
        formik.setFieldTouched("dateOfConstructionStart", true, false);
        formik.setFieldError("dateOfConstructionStart", "Välj ett startdatum");
        hasErrors = true;
      }

      if (!selectedEndDate) {
        formik.setFieldTouched("dateOfConstructionEnd", true, false);
        formik.setFieldError("dateOfConstructionEnd", "Välj ett slutdatum");
        hasErrors = true;
      }

      if (hasErrors) {
        return;
      }
    }

    setCurrentStep((prev) => (prev < 3 ? prev + 1 : prev));
    if (currentStep === 2) {
      setStepThreeErrors(emptyScaffoldingStepErrors());
    }
  };

  const prevStep = () => {
    if (currentStep === 3) {
      setStepThreeErrors(emptyScaffoldingStepErrors());
    }
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  return (
    <Container className="create-project-wizard-wrapper">
      <Row className="d-flex justify-content-center align-items-center text-center pt-3">
        <Col xl={12} lg={12} md={12} xs={12} className="d-flex justify-content-center">
          <h2 className="create-project-wizard-title">SKAPA PROJEKT</h2>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col>
          <div className="wizard-step-indicator" aria-label="Stegindikator">
            {[1, 2, 3].map((step, index) => (
              <React.Fragment key={step}>
                <div className={`wizard-step-dot ${currentStep === step ? "active" : ""}`}>
                  {step}
                </div>
                {index < 2 && <div className="wizard-step-connector" />}
              </React.Fragment>
            ))}
          </div>
        </Col>
      </Row>
      <div className="p-3">
        <Row className="d-flex justify-content-center">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              formik.submitForm();
            }}
            className="text-start"
          >
            <Col xl={12} lg={12} md={12} s={12} xs={12}>
              {currentStep === 1 && (
                <div className="create-project-step-card">
                  <Row className="d-flex justify-content-start align-items-center mb-3">
                    <Col>
                      <h4>PROJEKTNR</h4>
                      <Form.Control
                        plaintext
                        readOnly
                        name="projectNumber"
                        value={formik.values.projectNumber ?? ""}
                        onChange={formik.handleChange}
                        style={{ fontSize: "large" }}
                        className="ps-3 text-start"
                        isInvalid={!!formik.errors.projectNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.projectNumber}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>BESTÄLLARE</h4>
                      <Form.Control
                        plaintext
                        readOnly
                        value={props.data?.customerName ?? ""}
                        style={{ fontSize: "large" }}
                        className="ps-3"
                      />
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>PROJEKTNAMN</h4>
                      <Form.Control
                        plaintext
                        readOnly
                        name="projectName"
                        value={formik.values.projectName ?? ""}
                        onChange={formik.handleChange}
                        style={{ fontSize: "large" }}
                        className="ps-3"
                        isInvalid={!!formik.errors.projectName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.projectName}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>PROJEKTADRESS</h4>
                      <Form.Control
                        type="text"
                        name="projectAddress"
                        placeholder="Skriv in adressen"
                        value={formik.values.projectAddress ?? ""}
                        onChange={formik.handleChange}
                        className="ps-3"
                        isInvalid={formik.touched.projectAddress && !!formik.errors.projectAddress}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.projectAddress}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-between align-items-end my-3">
                    <Col>
                      <Row className="hstack mb-1">
                        <Col>
                          <h4>KONTAKTPERSON</h4>
                        </Col>
                        <Col className="d-flex justify-content-end">
                          <StbButton
                            variant="outline"
                            onClick={addContact}
                            className="text-center"
                          >
                            Lägg till kontakt
                          </StbButton>
                          <StbButton
                            variant="outline"
                            onClick={editContact}
                            className="text-center ms-2"
                            disabled={props.customerContactList.length === 0}
                          >
                            Ändra kontakt
                          </StbButton>
                        </Col>
                      </Row>
                      <Form.Group controlId="formGridContacts">
                        <Select
                          classNames={{ popup: { root: "custom-dropdown-style" } }}
                          size="large"
                          mode="multiple"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label?.props?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          style={{ width: "100%" }}
                          placeholder="Välj kontaktpersoner"
                          onChange={handleChangeContact}
                          value={props.selectedContactsIds}
                          getPopupContainer={getPopupContainer}
                          styles={{ popup: { root: { zIndex: 9999 } } }}
                          options={props.customerContactList.map((contact) => ({
                            label: (
                              <span style={contact.imported ? { fontSize: '18px' } : { fontStyle: 'italic', fontSize: '16px' }}>
                                {`${contact.name} (${(contact.phoneNumbers || []).join(", ")}) ${(contact.emailAddresses || []).join(", ")}`}
                              </span>
                            ),
                            value: contact.id,
                          }))}
                        />
                        {formik.touched.contacts && formik.errors.contacts && (
                          <div className="text-danger mt-1">
                            {formik.errors.contacts}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  {props.addContactErrorMessage && (
                    <Row>
                      <h6 style={{ color: "red" }}>
                        Kontaktpersonen har INTE lagts till!{" "}
                        {props.addContactErrorMessage}
                      </h6>
                    </Row>
                  )}
                </div>
              )}

              {currentStep === 2 && (
                <div className="create-project-step-card">
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>AVDELNING</h4>
                      <Dropdown>
                        <Dropdown.Toggle as={StbButton} variant="outline" id="department-dropdown">
                          {selectedDepartmentName || "Välj Avdelning"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {selectableDepartments.map((dep) => (
                            <Dropdown.Item
                              key={dep.value}
                              onClick={() => {
                                formik.setFieldValue("department", dep.value);
                                formik.setFieldError("department", undefined);
                              }}
                            >
                              {dep.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      {formik.errors.department && (
                        <div className="text-danger mt-1">{formik.errors.department}</div>
                      )}
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>ARBETSLEDARE</h4>
                      <Dropdown>
                        <Dropdown.Toggle as={StbButton} variant="outline" id="dropdown-basic">
                          {(teamLeaders as STBUser[] | undefined)?.find(tl => tl.id === formik.values.teamLeaderId)?.name || "Välj Arbetsledare"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {isLoadingTeamLeaders ? (
                            <Dropdown.Item disabled>Hämtar...</Dropdown.Item>
                          ) : (
                            (teamLeaders as STBUser[] | undefined)?.map((teamLeader: STBUser) => (
                              <Dropdown.Item
                                key={teamLeader.id}
                                onClick={() => {
                                  formik.setFieldValue(
                                    "teamLeaderId",
                                    teamLeader.id
                                  );
                                  formik.setFieldError("teamLeaderId", undefined);
                                }}
                              >
                                {teamLeader.name}
                              </Dropdown.Item>
                            ))
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                      {formik.errors.teamLeaderId && (
                        <div className="text-danger mt-1">{formik.errors.teamLeaderId}</div>
                      )}
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>STARTDATUM</h4>
                      <div className="create-project-datebox">
                        <DateBox
                          defaultValue={selectedDate}
                          onChange={(value) => handleDateChange(value)}
                        />
                      </div>
                      {formik.errors.dateOfConstructionStart && (
                        <div className="text-danger mt-1">
                          {formik.errors.dateOfConstructionStart}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>SLUTDATUM</h4>
                      <div className="create-project-datebox">
                        <DateBox
                          defaultValue={selectedEndDate}
                          onChange={(value) => handleEndDateChange(value)}
                        />
                      </div>
                      {formik.errors.dateOfConstructionEnd && (
                        <div className="text-danger mt-1">
                          {formik.errors.dateOfConstructionEnd}
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {currentStep === 3 && (
                <div className="create-project-step-card">
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>ANTAL KVADRATMETER</h4>
                      <Form.Control
                        type="number"
                        min="0"
                        value={squareMeters || ''}
                        onChange={(e) => setSquareMeters(e.target.value ? Number(e.target.value) : 0)}
                        className="ps-3"
                        style={{ width: '165px' }}
                      />
                      {stepThreeErrors.squareMeters && (
                        <div className="text-danger mt-1">{stepThreeErrors.squareMeters}</div>
                      )}
                    </Col>
                  </Row>

                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>ANTAL TILLTRÄDESLEDER</h4>
                      <Form.Control
                        type="number"
                        min="0"
                        value={accessRoutes || ''}
                        onChange={(e) => setAccessRoutes(e.target.value ? Number(e.target.value) : 0)}
                        className="ps-3"
                        style={{ width: '165px' }}
                      />
                      {stepThreeErrors.accessRoutes && (
                        <div className="text-danger mt-1">{stepThreeErrors.accessRoutes}</div>
                      )}
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>TYP AV STÄLLNING</h4>
                      <Form.Control
                        type="text"
                        placeholder=""
                        value={scaffoldingType}
                        onChange={(e) => setScaffoldingType(e.target.value)}
                        className="ps-3"
                      />
                      {stepThreeErrors.scaffoldingType && (
                        <div className="text-danger mt-1">{stepThreeErrors.scaffoldingType}</div>
                      )}
                    </Col>
                  </Row>

                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <h4>STÄLLNING AVSEDD FÖR TYP AV ARBETE</h4>
                      <Form.Control
                        type="text"
                        placeholder=""
                        value={scaffoldingIntendedFor}
                        onChange={(e) => setScaffoldingIntendedFor(e.target.value)}
                        className="ps-3"
                      />
                      {stepThreeErrors.scaffoldingIntendedFor && (
                        <div className="text-danger mt-1">{stepThreeErrors.scaffoldingIntendedFor}</div>
                      )}
                    </Col>
                  </Row>

                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col md={6}>
                      <h4>STÄLLNINGSBREDD</h4>
                      <Form.Control
                        type="text"
                        placeholder=""
                        value={scaffoldingWidth}
                        onChange={(e) => setScaffoldingWidth(e.target.value)}
                        className="ps-3"
                      />
                      {stepThreeErrors.scaffoldingWidth && (
                        <div className="text-danger mt-1">{stepThreeErrors.scaffoldingWidth}</div>
                      )}
                    </Col>
                    <Col md={6}>
                      <h4>HÖJD</h4>
                      <Form.Control
                        type="text"
                        placeholder=""
                        value={scaffoldingHeight}
                        onChange={(e) => setScaffoldingHeight(e.target.value)}
                        className="ps-3"
                      />
                      {stepThreeErrors.scaffoldingHeight && (
                        <div className="text-danger mt-1">{stepThreeErrors.scaffoldingHeight}</div>
                      )}
                    </Col>
                  </Row>

                  <Row className="d-flex justify-content-start align-items-center my-3">
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="includeMonteringsanalys"
                        label="Inkludera monteringsanalys"
                        checked={includeMonteringsanalys}
                        onChange={(e) => setIncludeMonteringsanalys(e.target.checked)}
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {currentStep < 3 ? (
                <Row className="d-flex justify-content-center align-items-center my-3">
                  <Col className="d-flex justify-content-between">
                    {currentStep > 1 ? (
                      <StbButton variant="secondary" onClick={prevStep}>
                        Tillbaka
                      </StbButton>
                    ) : (
                      <StbButton variant="secondary" onClick={goBack}>
                        Tillbaka
                      </StbButton>
                    )}
                    <StbButton variant="primary" onClick={nextStep}>
                      Nästa
                    </StbButton>
                  </Col>
                </Row>
              ) : (
                <>
                  <Row className="d-flex align-items-center my-3">
                    <Col className="d-flex justify-content-start">
                      <StbButton variant="secondary" onClick={prevStep}>
                        Tillbaka
                      </StbButton>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-center align-items-center my-3">
                    <Col className="d-flex justify-content-center">
                      <StbButton type="submit" variant="primary">
                        Skapa projekt
                      </StbButton>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Form>
        </Row>
      </div>
    </Container>
  );
};

export default CreateProjectForm;
